const API = "";

export interface MarketplaceStats {
  sellerCount: number;
  listingCount: number;
  totalValue: number;
}


export interface DiscountTier {
  minQty: number;
  pct:    number;
}

export interface ListingItem {
  id: number;
  category:     string;
  subcategory:  string | null;
  listingTitle: string | null;
  tubeName: string;
  brand: string | null;
  quantity: number;
  condition: string | null;
  askingPrice: string;
  askingPriceBasis: "per_unit" | "whole_lot";
  tier: string;
  tested: boolean;
  testDevice:          string | null;
  testValue:           string | null;
  testValue2:          string | null;
  testValue3:          string | null;
  testUnit:            string | null;
  testMinimum:         string | null;
  testGasValue:        string | null;
  testedGas:           boolean;
  testedMicrophonics:  boolean;
  testedShorts:        boolean;
  listingDescription:  string | null;
  notes:               string | null;
  sellerUsername: string;
  sellerName: string | null;
  isFounder?: boolean;
  shipsFromCity:  string | null;
  shipsFromState: string | null;
  shipsFromZip:   string | null;
  freeShipping:   boolean;
  discountTiers:    string | null;   // JSON DiscountTier[]
  bestOfferEnabled: boolean;
  firstPhoto:       string | null;   // base64 data URL of first listing photo
  photoCount:       number;          // total number of listing photos
}

export interface TubeSetListingItem {
  setId: number;
  tubeName: string;
  brand: string | null;
  quantityRequired: number;
  quantity: number;
}

export interface TubeSetListing {
  id: number;
  name: string;
  description: string | null;
  price: string;
  status: string;
  notes: string | null;
  sellerUsername: string;
  sellerName: string | null;
  isFounder?: boolean;
  items: TubeSetListingItem[];
  createdAt: string;
}

export interface ListingsPage {
  items: ListingItem[];
  total: number;
  page: number;
  limit: number;
  /** Rotation seed echoed back for the default "Fresh Mix" sort. */
  seed?: number;
}

/** Full listing detail — returned by GET /api/marketplace/listings/:id */
export interface ListingDetail {
  id: number;
  category:           string;
  subcategory:        string | null;
  listingTitle:       string | null;
  tubeName:           string;
  brand:              string | null;
  quantity:           number;
  condition:          string | null;
  tested:             boolean;
  testDevice:         string | null;
  testValue:          string | null;   // Value 1
  testValue2:         string | null;   // Value 2
  testValue3:         string | null;   // Value 3
  testUnit:           string | null;   // GM / %
  testMinimum:        string | null;   // minimum acceptable spec (same unit)
  testGasValue:       string | null;   // gas/leakage reading
  testedGas:          boolean;
  testedMicrophonics: boolean;
  testedShorts:       boolean;
  askingPrice:        string;
  askingPriceBasis:   "per_unit" | "whole_lot";
  tier:               string;
  listingDescription: string | null;
  notes:              string | null;
  photos:             string[];          // all listing photos (may be base64 data URIs)
  firstPhoto:         string | null;
  freeShipping:       boolean;
  shipsFromCity:      string | null;
  shipsFromState:     string | null;
  shipsFromZip:       string | null;
  discountTiers:      string | null;
  sellerUsername:     string;
  sellerName:         string | null;
  isFounder:          boolean | null;
  contactEmail:       string | null;
  contactPhone:       string | null;
  bestOfferEnabled:   boolean;
  autoAcceptPrice:    string | null;
  createdAt:          string;
}

export interface BuyerOfferInput {
  buyerName:   string;
  buyerEmail:  string;
  offerAmount: number;
}

export interface BuyerOfferResult {
  accepted: boolean;
  offer: {
    id:          number;
    listingId:   number;
    buyerEmail:  string;
    buyerName:   string;
    offerAmount: string;
    status:      string;
    createdAt:   string;
  };
}

export interface OfferCountResult {
  count:     number;
  remaining: number;
}

export interface BuyerRegistrationInput {
  name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

export interface SellerContactInfo {
  username: string;
  displayName: string | null;
  shopUrl: string;
}

async function get<T>(path: string): Promise<T> {
  const r = await fetch(`${API}${path}`);
  if (!r.ok) {
    const body = await r.json().catch(() => ({ error: r.statusText }));
    throw new Error((body as { error: string }).error ?? r.statusText);
  }
  return r.json() as Promise<T>;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const r = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => ({ error: r.statusText })) as { error?: string };
  if (!r.ok) throw new Error(data.error ?? r.statusText);
  return data as T;
}

export interface GearTubeReq {
  name: string;
  qty:  number;
}

export interface GearCatalogItem {
  id:             number;
  category:       string;
  brand:          string;
  model:          string;
  era:            string;
  type:           string;
  totalTubeCount: number;
  notes:          string;
  tubes:          GearTubeReq[];
}

export interface GearCatalog {
  gear:       GearCatalogItem[];
  categories: Record<string, string>;
}

export function parseDiscountTiers(raw: string | null | undefined): DiscountTier[] {
  if (!raw) return [];
  try { return JSON.parse(raw) as DiscountTier[]; }
  catch { return []; }
}

export function getBestDiscount(tiers: DiscountTier[], qty: number): number {
  if (!tiers.length || qty <= 1) return 0;
  const sorted = [...tiers].sort((a, b) => b.minQty - a.minQty);
  return sorted.find(t => qty >= t.minQty)?.pct ?? 0;
}

export const CATEGORY_DISPLAY: Record<string, string> = {
  tubes:           "Tubes",
  parts:           "Parts",
  books:           "Books & Manuals",
  "tube-testers":  "Tube Testers",
  "tube-gear":     "Tube Gear",
};

export const SUBCATEGORY_DISPLAY: Record<string, string> = {
  "hi-fi":            "Hi-Fi",
  "guitar-amps":      "Guitar Amps",
  "comm-ham-cb":      "Ham / CB",
  "test-equipment":   "Test Equipment",
  "industrial":       "Industrial",
  "television":       "Television",
  "radio-receivers":  "Radio Receivers",
};

export const api = {
  getStats: () => get<MarketplaceStats>("/api/marketplace/stats"),
  getListings: (params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    return get<ListingsPage>(`/api/marketplace/listings${qs ? `?${qs}` : ""}`);
  },
  registerBuyer: (input: BuyerRegistrationInput) =>
    post<{ message: string }>("/api/marketplace/buyers/register", input),
  verifyToken: (token: string) =>
    get<{ message: string; verified: boolean; buyerEmail?: string }>(`/api/marketplace/buyers/verify/${token}`),
  getSellerContact: (username: string, token: string) =>
    get<SellerContactInfo>(`/api/marketplace/sellers/${encodeURIComponent(username)}/contact?token=${encodeURIComponent(token)}`),
  getSets: () => get<TubeSetListing[]>("/api/marketplace/sets"),
  getGearCatalog: () => get<GearCatalog>("/api/marketplace/gear"),
  getGearSets: (gearId: number) => get<TubeSetListing[]>(`/api/marketplace/gear/${gearId}/sets`),
  getGearListings: (gearId: number) => get<GearListingsResult>(`/api/marketplace/gear/${gearId}/listings`),
  getListing: (id: number) => get<ListingDetail>(`/api/marketplace/listings/${id}`),
  getOfferCount: (listingId: number, email: string) =>
    get<OfferCountResult>(`/api/marketplace/listings/${listingId}/my-offer-count?email=${encodeURIComponent(email)}`),
  submitOffer: (listingId: number, input: BuyerOfferInput) =>
    post<BuyerOfferResult>(`/api/marketplace/listings/${listingId}/offers`, input),
};

export interface GearTubeListing {
  id: number;
  tubeName: string;
  brand: string | null;
  quantity: number;
  condition: string | null;
  askingPrice: string;
  askingPriceBasis: "per_unit" | "whole_lot";
  tier: string;
  tested: boolean;
  sellerUsername: string;
  sellerName: string | null;
  isFounder?: boolean | null;
  shipsFromCity: string | null;
  shipsFromState: string | null;
  freeShipping: boolean;
  firstPhoto: string | null;
}

export interface GearListingsResult {
  gear: GearCatalogItem;
  tubeListings: Record<string, GearTubeListing[]>;
  matchedSets: TubeSetListing[];
}
